import React from "react"
import { type RouteObject } from "react-router-dom"

import { PermissionGate } from "../../../../components/PermissionGate/PermissionGate.tsx"
import { RoutesPaths } from "../../../../constants/routes.ts"
import AiCenterAvailabilityGate from "../../../AiCenter/AiCenterAvailabilityGate.tsx"
import { AiToolAvailabilityGate } from "../../../AiCenter/AiToolAvailabilityGate.tsx"
import { lazyPage } from "../lazyPage.tsx"

// Loaded on demand: a user without the flag never downloads the AI Center.
const AiCenterPage = lazyPage(
  () => import("../../../AiCenter/AiCenterPage.tsx")
)
const JobDetailPage = lazyPage(() =>
  import("../../../AiCenter/sections/Plans3dModel/detail/JobDetailPage.tsx").then(
    (module) => ({ default: module.JobDetailPage })
  )
)
const EstimateWorkspacePage = lazyPage(() =>
  import("../../../AiCenter/sections/PlansToEstimate/estimate/EstimateWorkspacePage.tsx").then(
    (module) => ({ default: module.EstimateWorkspacePage })
  )
)
const PriceBookPage = lazyPage(() =>
  import("../../../AiCenter/sections/PlansToEstimate/PriceBookPage.tsx").then(
    (module) => ({ default: module.PriceBookPage })
  )
)
const ReceivablesWorkspacePage = lazyPage(() =>
  import("../../../AiCenter/sections/CustomerSowReceivables/workspace/ReceivablesWorkspacePage.tsx").then(
    (module) => ({ default: module.ReceivablesWorkspacePage })
  )
)

// Behind the ai_center flag until the AI Center is productized, then each screen
// behind the View right its endpoints require.
const ESTIMATE_VIEW = "ai_center.plans_estimate.plans_estimate_view"
const PLANS_3D_VIEW = "ai_center.plans_3d.plans_3d_view"
const SOW_RECEIVABLES_VIEW = "ai_center.sow_receivables.sow_receivables_view"
const BID_FROM_SOW_VIEW = "ai_center.bid_from_sow.bid_from_sow_view"

const gated = (page: React.ReactNode) => (
  <AiCenterAvailabilityGate>{page}</AiCenterAvailabilityGate>
)

export const aiCenterRoutes: RouteObject[] = [
  {
    path: RoutesPaths.aiCenter,
    element: gated(
      <PermissionGate
        mode="some"
        paths={[
          "ai_center.plans_sow.plans_sow_view",
          ESTIMATE_VIEW,
          PLANS_3D_VIEW,
          SOW_RECEIVABLES_VIEW,
          BID_FROM_SOW_VIEW,
          "ai_center.receipts.receipt_view"
        ]}
      >
        <AiCenterPage />
      </PermissionGate>
    )
  },
  {
    path: RoutesPaths.aiCenterPriceBook,
    element: gated(
      <PermissionGate paths={[ESTIMATE_VIEW]}>
        <AiToolAvailabilityGate tool="plans-estimate">
          <PriceBookPage />
        </AiToolAvailabilityGate>
      </PermissionGate>
    )
  },
  {
    path: RoutesPaths.aiCenterEstimate(":documentId"),
    element: gated(
      <PermissionGate paths={[ESTIMATE_VIEW]}>
        <AiToolAvailabilityGate tool="plans-estimate">
          <EstimateWorkspacePage />
        </AiToolAvailabilityGate>
      </PermissionGate>
    )
  },
  {
    path: RoutesPaths.aiCenter3dModel(":jobId"),
    element: gated(
      <PermissionGate paths={[PLANS_3D_VIEW]}>
        <AiToolAvailabilityGate tool="plans-3d-model">
          <JobDetailPage />
        </AiToolAvailabilityGate>
      </PermissionGate>
    )
  },
  {
    path: RoutesPaths.aiCenterSowReceivables(":analysisId"),
    element: gated(
      <PermissionGate paths={[SOW_RECEIVABLES_VIEW]}>
        <ReceivablesWorkspacePage />
      </PermissionGate>
    )
  }
]
